/** @jsx jsx */
import { React, jsx, css, getAppStore, portalUrlUtils, SessionManager, esri, hooks, dateUtils, i18n, focusElementInKeyboardMode } from 'jimu-core';
import { Button } from './button';
import { Loading } from './loading';
import { LoadingType } from './types';
import { TextInput } from './text-input';
import { Select } from './select';
import defaultMessages from '../translations/default';
import { FloatingPanel } from './overlay';
function getStyle() {
    return css `
    >.floating-header.panel-header {
      border: 1px solid var(--sys-color-secondary-light);
      padding: 14px 20px !important;
      font-size: 1rem;
      font-weight: 600;
      background: var(--ref-palette-white);
      color: var(--ref-palette-neutral-1100);
      .action-btn {
        color: var(--ref-palette-neutral-1100);
      }
      >.actions>.jimu-btn :hover {
        color: var(--ref-palette-neutral-1100);
      }
    }
    .add-item-container {
      padding: 16px 20px 20px;
      .form-item-label {
        line-height: 18px;
        font-weight: 500;
        color: var(--ref-palette-black);
      }
    }
    .footer{
      margin-top: 24px;
      text-align: right;
      .btn {
        font-weight: 500;
      }
    }
  `;
}
/**
 * The `AddItemDialog` component let users to set item's title and folder.
 *
 * ```ts
 * import { AddItemDialog } from 'jimu-ui'
 * ```
 */
export function AddItemDialog(props) {
    const { defaultName, reference, onClose, onConfirm } = props;
    const [folders, setFolders] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [name, setName] = React.useState('');
    const [folderId, setFolderId] = React.useState(null);
    const textRef = React.useRef();
    const translate = hooks.useTranslation(defaultMessages);
    const [isExporting, setIsExporting] = React.useState(false);
    const [itemId, setItemId] = React.useState('');
    const getReadableTime = () => {
        const date = new Date();
        const offset = date.getTimezoneOffset();
        const offsetStr = offset < 0 ? `+${Math.abs(offset) / 60}` : `-${offset / 60}`;
        return `${dateUtils.formatDateValueByEsriFormat(date, 'shortDateShortTime', i18n.getIntl())} ${translate('utc')}${offsetStr}`;
    };
    hooks.useEffectOnce(() => {
        const sm = SessionManager.getInstance();
        sm.getUserInfo().then(userInfo => {
            if (!userInfo) {
                return;
            }
            const portalUrl = getAppStore().getState().portalUrl;
            esri.restRequest.request(portalUrlUtils.getUserContentUrl(portalUrl, userInfo.username), {
                authentication: sm.getMainSession(),
                httpMethod: 'GET'
            }).then(userContent => {
                const folders = userContent.folders;
                setFolders([{ id: '', title: userInfo.username }, ...folders]);
                setFolderId('');
            }).catch(e => {
                console.warn(e, 'get user content error');
            });
        }).finally(() => {
            focusElementInKeyboardMode(textRef.current, true);
            setIsLoading(false);
        });
        if (defaultName) {
            setName(`${defaultName} (${getReadableTime()})`);
        }
    });
    const handleNameChange = hooks.useEventCallback((e) => {
        setName(e.target.value);
    });
    const handleFolderChange = hooks.useEventCallback((e) => {
        setFolderId(e.target.value);
    });
    const handleOkClick = React.useCallback(() => {
        if (itemId) {
            let portalUrl = getAppStore().getState().portalUrl;
            portalUrl = portalUrlUtils.getStandardPortalUrl(portalUrl);
            const itemUrl = `${portalUrl}/home/item.html?id=${itemId}`;
            window.open(itemUrl, '_blank');
            return;
        }
        setIsExporting(true);
        onConfirm(name, folderId).then((itemId) => {
            setItemId(itemId);
            setIsExporting(false);
        });
    }, [itemId, onConfirm, name, folderId]);
    return (jsx(FloatingPanel, { open: true, headerTitle: translate('saveAsAnItem'), css: getStyle(), onHeaderClose: onClose, disableResize: true, dragBounds: "body", defaultSize: { width: 300, height: 270 }, reference: reference, toggle: (evt, type) => { type !== 'clickOutside' && (onClose === null || onClose === void 0 ? void 0 : onClose()); } },
        jsx("div", { className: "add-item-container h-100" },
            jsx("div", null,
                jsx("div", null,
                    jsx("div", { className: 'mb-1 form-item-label' }, translate('title')),
                    jsx("div", null,
                        jsx(TextInput, { required: true, value: name, ref: textRef, onChange: handleNameChange, disabled: isExporting || !!itemId }))),
                jsx("div", { className: 'mt-4' },
                    jsx("div", { className: 'mb-1 form-item-label' }, translate('folder')),
                    jsx("div", null,
                        jsx(Select, { value: folderId, onChange: handleFolderChange, disabled: isExporting || !!itemId }, folders.map(f => jsx("option", { key: f.id, value: f.id }, f.title)))),
                    isLoading && jsx(Loading, { type: LoadingType.Bar }))),
            jsx("div", { className: 'footer' },
                jsx(Button, { type: itemId ? 'default' : 'primary', disabled: isLoading || isExporting || !name, onClick: handleOkClick }, itemId ? translate('viewItem') : isExporting ? translate('exporting') : translate('export')),
                !itemId && jsx(Button, { className: 'ml-2', disabled: isLoading, onClick: onClose }, translate('cancel'))))));
}
